/**
 * Shared constants, style maps and pure utility functions for the DI panel.
 * Imported by all sub-components to avoid duplication.
 */

export const QUAL_LABELS = {
  instr_teorica: "Instrutor Teórica",
  instr_de_instrutores: "Instrutor de Instrutores",
  examinador: "Examinador",
};

export const QUAL_NO_DATES = new Set(["instr_teorica"]);

export const FLIGHT_TYPE_LABELS = {
  instrucao: "Instrução",
  solo_supervisionado: "Solo supervisionado",
  voo_solo: "Voo solo",
  viagem: "Particular",
  uniformizacao: "Uniformização",
};

export const SOLO_FLIGHT_TYPES = new Set(["solo_supervisionado", "largada_solo", "voo_solo"]);

// Etiqueta do acompanhante num voo: num voo a solo o aluno é PIC; nos outros voos de piloto é DC
export function companionRoleLabel(f) {
  if (f?.companion_kind === "instructor") return "2º INSTR";
  if (f?.companion_kind === "external") return "AVULSO";
  return SOLO_FLIGHT_TYPES.has(f?.flight_type) ? "PIC" : "DC";
}
export const REQUIRES_INSTRUCTOR_HOURS = new Set(["instrucao", "solo_supervisionado", "largada_solo", "voo_solo"]);
export const FORBIDS_INSTRUCTOR_HOURS = new Set(["uniformizacao", "viagem"]);

export function classifyFlight(f) {
  if (f.role === "companion") return "dc-received";
  const instrMin = Number(f.instructor_minutes || 0);
  if (REQUIRES_INSTRUCTOR_HOURS.has(f.flight_type) && instrMin <= 0) return "instruction-missing";
  if (FORBIDS_INSTRUCTOR_HOURS.has(f.flight_type) && instrMin > 0) return "instruction-forbidden";
  if (instrMin > 0) return "instruction";
  return "normal";
}

export const FLIGHT_ROW_DESKTOP_CLASS = {
  "dc-received":           "bg-teal-500/[0.08] hover:bg-teal-500/15",
  "instruction-missing":   "bg-red-500/10 hover:bg-red-500/15",
  "instruction-forbidden": "bg-red-500/10 hover:bg-red-500/15",
  instruction:             "bg-blue-500/[0.08] hover:bg-blue-500/15",
  normal: "",
};
export const FLIGHT_ROW_MOBILE_CLASS = {
  "dc-received":           "border-teal-500/40 bg-teal-500/[0.08]",
  "instruction-missing":   "border-red-500/40 bg-red-500/10",
  "instruction-forbidden": "border-red-500/40 bg-red-500/10",
  instruction:             "border-blue-500/40 bg-blue-500/[0.08]",
  normal:                  "border-cockpit-border bg-slate-900/40",
};
export const FLIGHT_BADGE_LABEL = {
  "dc-received":           "DC RECEBIDO",
  "instruction-missing":   "⚠ falta instrução",
  "instruction-forbidden": "⚠ instrução não permitida",
};

export const DEFAULT_AIRCRAFT = "CS-UTK";

export const STATUS_META = {
  green: { label: "Apto",      color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/40", dot: "bg-emerald-400" },
  amber: { label: "Atenção",   color: "text-amber-300",   bg: "bg-amber-500/15",   border: "border-amber-500/40",   dot: "bg-amber-400" },
  red:   { label: "Não apto",  color: "text-red-300",     bg: "bg-red-500/15",     border: "border-red-500/40",     dot: "bg-red-400" },
};

export const TONE_CLS = {
  ok:      "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  amber:   "border-amber-500/40 bg-amber-500/10 text-amber-300",
  red:     "border-red-500/40 bg-red-500/10 text-red-300",
  neutral: "border-slate-700 bg-slate-800/40 text-slate-300",
};

export const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
export const todayStr = () => new Date().toISOString().slice(0, 10);

export const minutesToHM = (min) => {
  const m = Math.max(0, parseInt(min || 0, 10));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h${String(mm).padStart(2, "0")}`;
};

export const hoursToHHmm = (decimal_h) => {
  const v = Number(decimal_h);
  if (!isFinite(v) || v <= 0) return "00:00";
  const totalMin = Math.round(v * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const yearsDecimalToYM = (decimal_y) => {
  if (decimal_y == null) return "—";
  const totalMonths = Math.max(0, Math.round(Number(decimal_y) * 12));
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  if (y === 0 && m === 0) return "0m";
  if (y === 0) return `${m}m`;
  if (m === 0) return `${y}a`;
  return `${y}a ${m}m`;
};

export const minutesToHHmmInput = (minutes) => {
  const m = Math.max(0, parseInt(minutes || 0, 10));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, "0")}`;
};

export const hhmmInputToMinutes = (str) => {
  if (str == null) return 0;
  const s = String(str).trim();
  if (!s) return 0;
  if (!s.includes(":")) {
    const n = parseFloat(s);
    return isFinite(n) && n >= 0 ? Math.round(n * 60) : 0;
  }
  const [hh, mm = "0"] = s.split(":");
  const h = parseInt(hh, 10);
  const m = parseInt(mm, 10);
  if (!isFinite(h) || !isFinite(m) || h < 0 || m < 0 || m >= 60) return 0;
  return h * 60 + m;
};

export function InfoChip({ label, value, tone = "neutral" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${TONE_CLS[tone] || TONE_CLS.neutral}`}
      title={`${label}: ${value}`}
    >
      <span className="opacity-60 text-[10px] uppercase tracking-wider">{label}</span>
      <span className="font-mono">{value}</span>
    </span>
  );
}
